import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

const Index = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (isMounted) {
          setSession(session);
          
          // Defer profile check to avoid blocking auth callback
          if (session) {
            setTimeout(() => {
              checkProfileAndNavigate(session);
            }, 0);
          } else {
            setIsChecking(false);
            navigate("/auth", { replace: true });
          }
        }
      }
    );

    // THEN check for existing session
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (isMounted) {
        setSession(session);
        
        if (session) {
          await checkProfileAndNavigate(session);
        } else {
          setIsChecking(false);
          navigate("/auth", { replace: true });
        }
      }
    };

    const checkProfileAndNavigate = async (session: Session) => {
      if (!isMounted) return;
      
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("approval_status")
          .eq("id", session.user.id)
          .single();

        if (!isMounted) return;

        if (profile?.approval_status === "pending" || profile?.approval_status === "rejected") {
          navigate("/pending-approval", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        console.error("Error checking profile:", error);
        if (isMounted) {
          navigate("/dashboard", { replace: true });
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md p-8 space-y-6 animate-fade-in">
          <div className="space-y-2 text-center">
            <Skeleton className="h-12 w-12 rounded-full mx-auto" />
            <Skeleton className="h-6 w-3/4 mx-auto" />
            <Skeleton className="h-4 w-1/2 mx-auto" />
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <Skeleton className="h-10 w-full" />
          </div>
          
          <div className="space-y-2">
            <Skeleton className="h-px w-full" />
            <Skeleton className="h-4 w-32 mx-auto" />
          </div>
        </Card>
      </div>
    );
  }

  return null;
};

export default Index;
